import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../api";

export interface RolePermission {
  module_id: number;
  module_name: string;
  permission_id: number;
  permission_name: string;
  module_sort_order: number;
  permission_sort_order: number;
}

export interface Role {
  role_id: number;
  role_name: string;
  role_description: string;
  role_status_flg: string;
  created_date: string;
  no_of_users: number;
  permissions: RolePermission[];
}

interface RoleListResponse {
  statusCode: number;
  message: string;
  data: {
    roles: Role[];
    links?: Record<string, string>;
    meta?: {
      current_page: number;
      from: number;
      last_page: number;
      path: string;
      per_page: number;
      to: number;
      total: number;
    };
  };
}

export const fetchRoles = async (
  params?: Record<string, any>
): Promise<RoleListResponse["data"]> => {
  try {
    const { data } = await api.get<RoleListResponse>("/roles/v1", {
      params,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            searchParams.append(key, `[${value.join(",")}]`);
          } else if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        });
        return searchParams.toString();
      },
    });

    return data.data;
  } catch (err: any) {
    // If 404, return empty list
    if (err.response?.status === 404) {
      return {
        roles: [],
        links: {},
      };
    }
    // Otherwise, rethrow error
    throw err;
  }
};

export const useRoles = (params?: Record<string, any>) =>
  useQuery({
    queryKey: ["roles", params],
    queryFn: () => fetchRoles(params),
    staleTime: 5 * 60 * 1000,
    keepPreviousData: true,
  });

export interface RoleUpdatePayload {
  action: "create" | "update" | "delete";
  role_id?: number;
  role_name?: string;
  role_description?: string;
  role_status_flg?: number;
  role_ids?: number[];
  permissions?: { module_id: number; permission_id: number }[];
}

interface RoleUpdateResponse {
  statusCode: number;
  message: string;
  data?: { role_id?: number; deleted_count?: number };
}

export const useUpdateRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: RoleUpdatePayload) => {
      const { data } = await api.post<RoleUpdateResponse>("/roles/v1", payload);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries(["roles"]),
  });
};
